import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useFormField,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'
import { TimeWindows } from './components/TimeWindows'
import { useEffect } from 'react'

export interface TimeWindowProps {
  testId?: string
  label: string
}

export type TimeWindowFieldValue = uui.domain.rm.TimeWindow[]
const defaultComponentValue: TimeWindowFieldValue = []

type TimeWindowFieldValueCouple = [uui.domain.rm.TimeWindow?, uui.domain.rm.TimeWindow?]
type TimeWindowFieldValueCoupleOnChange = (value: TimeWindowFieldValueCouple) => void

export type TimeWindowFieldProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = TimeWindowFieldValue,
> = {
  timeWindowProps: TimeWindowProps
} & TidalFormBridgeOptions<TimeWindowFieldValue, TargetField, State, AcceptedFormValue>

export function TimeWindowField<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = TimeWindowFieldValue,
>(props: TimeWindowFieldProps<TargetField, State, AcceptedFormValue>) {
  const {
    timeWindowProps: { testId, label },
    initialValue = defaultComponentValue as FormFieldDescriptor<State>[TargetField]['value'],
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const {
    fieldInfo: {
      value,
      onChange,
      fieldDisabled,
      errorOrHelperText,
      showErrorAccent,
      fieldHasError,
    },
  } = useTidalFormBridge<TimeWindowFieldValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  const {
    fieldApi: { validate },
  } = useFormField<TargetField, State>(props.name)

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      if (value.length > 2) {
        console.error('TimeWindowsField: value has more than 2 elements')
      }
    }
  }, [value])

  return (
    <TimeWindows
      testId={testId}
      label={label}
      value={value as TimeWindowFieldValueCouple}
      disabled={fieldDisabled}
      onChange={onChange as TimeWindowFieldValueCoupleOnChange}
      validate={validate}
      error={showErrorAccent && fieldHasError}
      helperText={errorOrHelperText}
    />
  )
}
